import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, Rocket } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });

export function UpgradeDialog({ open, onClose, pageId, onUpgraded }) {
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOpts(null);
    axios.get(`${API}/subscription/upgrade-options/${pageId}`, { headers: authHeaders() })
      .then(r => setOpts(r.data)).catch(() => toast.error("Paketdaten konnten nicht geladen werden"));
  }, [open, pageId]);

  const upgrade = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/subscription/upgrade`, { page_id: pageId }, { headers: authHeaders() });
      toast.success(`Upgrade auf ${res.data.package_name} erfolgreich!`);
      await onUpgraded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upgrade fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const t = opts?.target;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-indigo-600" />{t ? `Auf ${t.name} upgraden` : "Paket upgraden"}</DialogTitle>
          <DialogDescription>{t ? t.headline : "Lade Paketdaten …"}</DialogDescription>
        </DialogHeader>
        {!opts ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div> : !t ? (
          <p className="text-sm text-slate-600" data-testid="upgrade-none">Du hast bereits das höchste Paket.</p>
        ) : (
          <>
            <div className="bg-indigo-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-indigo-700">{t.price.toFixed(2).replace(".", ",")} € <span className="text-sm font-normal text-indigo-500">/ Monat</span></p>
              <ul className="mt-3 space-y-1.5">
                {t.features.map(f => <li key={f} className="flex gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{f}</li>)}
              </ul>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">Das Upgrade gilt sofort. Die anteilige Differenz für den laufenden Abrechnungszeitraum wird über deine hinterlegte Zahlungsmethode abgebucht, danach zahlst du den neuen Monatspreis. Schlägt die Zahlung fehl, bleibt dein bisheriges Paket bestehen.</p>
            {!opts.can_upgrade && <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3" data-testid="upgrade-blocked">{opts.reason || "Ein Upgrade ist für diese Seite aktuell nicht möglich."}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="upgrade-cancel">Abbrechen</Button>
              <Button onClick={upgrade} disabled={busy || !opts.can_upgrade} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="upgrade-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Jetzt auf ${t.name} upgraden`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
